import { create } from "zustand";
import {
  ICompanyAccess,
  IObjectCompanies,
} from "../../../types/companyaccess.type";
import { getActiveCompaniesList } from "@/common/services/company.api";

type FilterMode = "all" | "region" | "uf" | "cities";

export type SimpleCompanyCache = {
  id: string;
  name: string;
  sigla: string;
  active: boolean;
};

export type CompanyStore = {
  company: ICompanyAccess | null;
  selectedCompanies: IObjectCompanies[];
  selectedRegion: string | null;
  selectedUf: string | null;
  filterMode: FilterMode;
  availableCompanies: SimpleCompanyCache[];
  actions: {
    toggleCompanySelection: (company: IObjectCompanies) => void;
    setRegionSelection: (region: string) => void;
    setUfSelection: (uf: string) => void;
    setAllSelection: () => void;
    syncAvailableCompanies: () => Promise<void>;
  };
};

const initialCompanies = (): IObjectCompanies[] => {
  const stored = localStorage.getItem("selectedCompanies");
  return stored ? JSON.parse(stored) : [];
};

const initialUf = (): string | null => {
  const stored = localStorage.getItem("selectedUf");
  return stored ? JSON.parse(stored) : null;
};

const initialRegion = (): string | null => {
  const stored = localStorage.getItem("selectedRegion");
  return stored ? JSON.parse(stored) : null;
};

const initialFilterMode = (): FilterMode => {
  const stored = localStorage.getItem("filterMode") as FilterMode;
  return stored || "all";
};

const initialAvailableCompanies = (): SimpleCompanyCache[] => {
  const stored = localStorage.getItem("all_active_companies_cache");
  return stored ? JSON.parse(stored) : [];
};

const initialCompany = localStorage.getItem("selectedCompanies");

export const useCompanyStore = create<CompanyStore>((set) => ({
  company: initialCompany ? JSON.parse(initialCompany) : null,
  selectedCompanies: initialCompanies(),
  selectedRegion: initialRegion(),
  selectedUf: initialUf(),
  filterMode: initialFilterMode(),
  availableCompanies: initialAvailableCompanies(),
  actions: {
    toggleCompanySelection: (company) =>
      set((state) => {
        const isAlreadySelected = state.selectedCompanies.some(
          (c) => c.companyId === company.companyId
        );

        let newSelectedCompanies: IObjectCompanies[];

        if (isAlreadySelected) {
          newSelectedCompanies = state.selectedCompanies.filter(
            (c) => c.companyId !== company.companyId
          );
        } else {
          newSelectedCompanies = [...state.selectedCompanies, company];
        }

        const newMode = newSelectedCompanies.length > 0 ? "cities" : "all";

        localStorage.setItem(
          "selectedCompanies",
          JSON.stringify(newSelectedCompanies)
        );
        localStorage.setItem("filterMode", newMode);
        localStorage.removeItem("selectedRegion");
        localStorage.removeItem("selectedUf");

        return {
          selectedCompanies: newSelectedCompanies,
          selectedRegion: null,
          selectedUf: null,
          filterMode: newMode,
        };
      }),

    setRegionSelection: (region) => {
      localStorage.setItem("selectedRegion", JSON.stringify(region));
      localStorage.setItem("filterMode", "region");
      localStorage.removeItem("selectedCompanies");
      localStorage.removeItem("selectedUf");

      set({
        selectedCompanies: [],
        selectedRegion: region,
        selectedUf: null,
        filterMode: "region",
      });
    },

    setUfSelection: (uf) => {
      localStorage.setItem("selectedUf", JSON.stringify(uf));
      localStorage.setItem("filterMode", "uf");
      localStorage.removeItem("selectedCompanies");
      localStorage.removeItem("selectedRegion");

      set({
        selectedCompanies: [],
        selectedRegion: null,
        selectedUf: uf,
        filterMode: "uf",
      });
    },

    setAllSelection: () => {
      localStorage.setItem("filterMode", "all");
      localStorage.removeItem("selectedCompanies");
      localStorage.removeItem("selectedRegion");
      localStorage.removeItem("selectedUf");

      set({
        selectedCompanies: [],
        selectedRegion: null,
        selectedUf: null,
        filterMode: "all",
      });
    },

    syncAvailableCompanies: async () => {
      try {
        const response = await getActiveCompaniesList();

        if (response?.ok && Array.isArray(response.data)) {
          const companiesToCache: SimpleCompanyCache[] = response.data.map(
            (c) => ({
              id: c.id,
              name: c.name,
              sigla: c.sigla,
              active: c.active,
            })
          );

          localStorage.setItem(
            "all_active_companies_cache",
            JSON.stringify(companiesToCache)
          );

          set({ availableCompanies: companiesToCache });
        }
      } catch (error) {
        console.error("Falha ao sincronizar empresas:", error);
      }
    },
  },
}));

export const useCompanyStoreActions = () =>
  useCompanyStore((state) => state.actions);
